import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Users, User, Wrench, Package, Phone, Search, ArrowRight, AlertTriangle, Bookmark } from 'lucide-react';
import { SEARCH_TYPES, highlightMatches } from '../lib/globalSearchService';

const GlobalSearchResults = ({ 
  results, 
  searchTerm, 
  loading, 
  onResultClick, 
  onViewAll, 
  className = "" 
}) => {
  if (loading) {
    return (
      <div className={`search-results-container absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-[60] ${className}`}>
        <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
          Buscando...
        </div>
      </div>
    );
  }

  if (!searchTerm || searchTerm.trim().length < 2) {
    return null;
  }

  if (results.length === 0) {
    return (
      <div className={`search-results-container absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-[60] ${className}`}>
        <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
          No se encontraron resultados para "{searchTerm}"
        </div>
      </div>
    );
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case SEARCH_TYPES.ORDER:
        return <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
      case SEARCH_TYPES.CLIENT:
        return <Users className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case SEARCH_TYPES.TECHNICIAN:
        return <Wrench className="h-4 w-4 text-orange-600 dark:text-orange-400" />;
      case SEARCH_TYPES.EQUIPMENT:
        return <Package className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
      default:
        return <Search className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case SEARCH_TYPES.ORDER:
        return 'Orden';
      case SEARCH_TYPES.CLIENT:
        return 'Cliente';
      case SEARCH_TYPES.TECHNICIAN:
        return 'Técnico';
      case SEARCH_TYPES.EQUIPMENT:
        return 'Equipo';
      default:
        return 'Resultado';
    }
  };

  const truncateText = (text, maxLength = 60) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div 
      className={`search-results-container absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-[60] max-h-96 overflow-y-auto ${className}`}
    >
      {/* Search results header */}
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {results.length} {results.length === 1 ? 'resultado' : 'resultados'} encontrados
          </span>
          {results.length >= 10 && (
            <button
              onClick={onViewAll}
              className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium flex items-center space-x-1"
            >
              <span>Ver todos</span>
              <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Search results list */}
      <div className="py-1">
        {results.map((result, index) => (
          <Link
            key={`${result.type}-${result.id}-${index}`}
            to={result.url}
            onClick={() => onResultClick && onResultClick(result)}
            className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-50 dark:border-gray-700 last:border-b-0 touch-manipulation"
            style={{ touchAction: 'manipulation' }}
          >
            <div className="flex items-start space-x-3">
              {/* Type icon */}
              <div className="flex-shrink-0 mt-0.5">
                {getTypeIcon(result.type)}
              </div>
              
              {/* Result content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {getTypeLabel(result.type)}
                  </span>
                  {result.matchField && (
                    <>
                      <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {result.matchField}
                      </span>
                    </>
                  )}
                </div>
                
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-1 flex items-center space-x-2">
                  <span 
                    dangerouslySetInnerHTML={{ 
                      __html: highlightMatches(result.title, searchTerm) 
                    }} 
                  />
                  {result.type === SEARCH_TYPES.ORDER && result.isBookmarked && (
                    <Bookmark className="h-4 w-4 text-yellow-600 dark:text-yellow-400 fill-current flex-shrink-0" title="Orden favorita" />
                  )}
                  {result.type === SEARCH_TYPES.CLIENT && result.isBlacklisted && (
                    <div className="flex items-center space-x-1">
                      <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                      <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded font-medium">
                        Lista Negra
                        {result.blacklistIncidentsCount > 1 && (
                          <span className="ml-1">({result.blacklistIncidentsCount})</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
                
                {result.subtitle && (
                  <div className="text-xs text-gray-600 dark:text-gray-300 mb-1 flex items-center">
                    <span 
                      dangerouslySetInnerHTML={{ 
                        __html: highlightMatches(result.subtitle, searchTerm) 
                      }} 
                    />
                  </div>
                )}
                
                {result.description && result.description !== result.title && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <span 
                      dangerouslySetInnerHTML={{ 
                        __html: highlightMatches(truncateText(result.description), searchTerm) 
                      }} 
                    />
                  </div>
                )}
              </div>
              
              {/* Arrow indicator */}
              <div className="flex-shrink-0 mt-1">
                <ArrowRight className="h-3 w-3 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Footer with search tips */}
      {searchTerm.length >= 2 && (
        <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-1 mb-1">
              <Phone className="h-3 w-3" />
              <span>Busca por número de teléfono para encontrar clientes</span>
            </div>
            <div className="flex items-center space-x-1">
              <FileText className="h-3 w-3" />
              <span>Busca en observaciones y detalles de órdenes</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchResults;